import { useQuery } from '@tanstack/react-query';

import { queryClient } from '../components/queryClient';
import { ParsedRouteDefinition } from '../interfaces';

export interface UserKML {
  id: number;
  filename: string;
  data: ParsedRouteDefinition;
  visible: boolean;
}

const USER_KMLS_KEY = ['userKMLs'];

export const useKMLQuery = () => {
  const queryKey = ['kml'];

  const queryFn = () => Promise.resolve<ParsedRouteDefinition | null>(null);

  return useQuery({
    queryKey,
    queryFn,
    cacheTime: 1000 * 60 * 60 * 24, // 24h
    staleTime: 1000 * 60 * 60 * 24, // 24h
    refetchOnWindowFocus: false,
  });
};

export const useKMLFilename = () => {
  const queryKey = ['kmlFilename'];

  const queryFn = () => Promise.resolve<string | null>(null);

  return useQuery({
    queryKey,
    queryFn,
    cacheTime: 1000 * 60 * 60 * 24, // 24h
    staleTime: 1000 * 60 * 60 * 24, // 24h
    refetchOnWindowFocus: false,
  });
};

/**
 *
 * @param kml KML file contents to put into cache
 *
 * @description Use this once local KML file is loaded, to access it in all instances
 * of the map / street view components.
 */
export const setKMLqueryData = (prd: ParsedRouteDefinition) => {
  // Put data (or partial data) into cache
  queryClient.setQueryData(['kml'], prd);
};

/**
 *
 * @param kml KML file contents to put into cache
 *
 * @description Use this once local KML file is loaded, to access it in all instances
 * of the map / street view components.
 */
export const setKMLfilename = (name: string) => {
  queryClient.setQueryData(['kmlFilename'], name);
};

export const useUserKMLs = () =>
  useQuery({
    queryKey: USER_KMLS_KEY,
    queryFn: () => Promise.resolve<UserKML[]>([]),
    cacheTime: 1000 * 60 * 60 * 24,
    staleTime: 1000 * 60 * 60 * 24,
    refetchOnWindowFocus: false,
  });

/** Returns the current user KML list synchronously from cache. */
export const getUserKMLs = () => queryClient.getQueryData<UserKML[]>(USER_KMLS_KEY) ?? [];

export const addUserKML = (filename: string, data: ParsedRouteDefinition) => {
  queryClient.setQueryData(USER_KMLS_KEY, [...getUserKMLs(), { id: Date.now(), filename, data, visible: true }]);
};

export const toggleUserKMLVisibility = (id: number) => {
  queryClient.setQueryData(
    USER_KMLS_KEY,
    getUserKMLs().map((kml) => (kml.id === id ? { ...kml, visible: !kml.visible } : kml)),
  );
};

export const removeUserKML = (id: number) => {
  queryClient.setQueryData(
    USER_KMLS_KEY,
    getUserKMLs().filter((kml) => kml.id !== id),
  );
};

export interface LocalOverride {
  data: ParsedRouteDefinition;
  filename: string;
}

const LOCAL_OVERRIDES_KEY = ['localOverrides'];
const HIDDEN_ROUTE_IDS_KEY = ['hiddenRouteIds'];

export const useLocalOverrides = () =>
  useQuery({
    queryKey: LOCAL_OVERRIDES_KEY,
    queryFn: () => Promise.resolve<Record<number, LocalOverride>>({}),
    cacheTime: 1000 * 60 * 60 * 24,
    staleTime: 1000 * 60 * 60 * 24,
    refetchOnWindowFocus: false,
  });

export const getLocalOverrides = (): Record<number, LocalOverride> =>
  queryClient.getQueryData<Record<number, LocalOverride>>(LOCAL_OVERRIDES_KEY) ?? {};

export const setLocalOverride = (routeId: number, override: LocalOverride) => {
  queryClient.setQueryData(LOCAL_OVERRIDES_KEY, { ...getLocalOverrides(), [routeId]: override });
};

export const useHiddenRouteIds = () =>
  useQuery({
    queryKey: HIDDEN_ROUTE_IDS_KEY,
    queryFn: () => Promise.resolve<number[]>([]),
    select: (data) => new Set(data),
    cacheTime: 1000 * 60 * 60 * 24,
    staleTime: 1000 * 60 * 60 * 24,
    refetchOnWindowFocus: false,
  });

export const getHiddenRouteIds = (): Set<number> =>
  new Set(queryClient.getQueryData<number[]>(HIDDEN_ROUTE_IDS_KEY) ?? []);

export const toggleHiddenRouteId = (id: number) => {
  const current = queryClient.getQueryData<number[]>(HIDDEN_ROUTE_IDS_KEY) ?? [];
  const asSet = new Set(current);

  if (asSet.has(id)) {
    asSet.delete(id);
  } else {
    asSet.add(id);
  }

  queryClient.setQueryData(HIDDEN_ROUTE_IDS_KEY, [...asSet]);
};
